import { forwardRef } from "react";
import MuiIconButton from "@mui/material/IconButton";
import type { IconButtonProps as MuiIconButtonProps } from "@mui/material/IconButton";
import TooltipHelper from "components/TooltipHelper";
import type { TooltipHelperProps } from "components/TooltipHelper";

interface TooltipProps extends Pick<
  TooltipHelperProps,
  "placement" | "mode" | "disableFocusListener" | "disableHoverListener" | "disableTouchListener" | "onOpen" | "onClose"
> {}

interface IconButtonProps extends MuiIconButtonProps {
  /**
   * @description The title to be displayed in the tooltip comes from icon button's title prop.
   * If not provided, no tooltip will be shown.
   */
  tooltipProps?: Omit<TooltipProps, "title">;
}

/**
 * @description A wrapper around MUI's IconButton that adds tooltip functionality.
 * If a title is provided (IconButton title), it will display a tooltip on hover.
 *
 * Tooltip placement is top by default but can be customized via tooltipProps.
 * The ref is forwarded to the underlying MUI IconButton so this component can be used
 * as the anchor of an external Tooltip (which needs a ref for positioning).
 */
const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(props, ref) {
  const { tooltipProps, ...rest } = props;

  /** If disabled, do not show tooltip (as per MUI Tooltip behavior). */
  if (props.disabled) {
    return <MuiIconButton ref={ref} {...rest} />;
  }
  return (
    <TooltipHelper disableHoverListener={!props.title} placement="top" {...tooltipProps} title={props.title}>
      <MuiIconButton ref={ref} {...rest} title={undefined} />
    </TooltipHelper>
  );
});

export default IconButton;
